import { Footer } from "@/parts/footer";
import { Meta } from "@/parts/meta";
import { Pt } from "@/parts/pt";
import { Breadcrumbs } from "@/parts/breadcrumbs";
import { client, getClient, urlFor } from "@/utils/client";
import type { GetStaticPropsContext } from "next";
import { settingsQuery } from "@/utils/queries";
import { IBlogPost, ISettings } from "@/utils/types";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import Image from "next/image";
import { useRouter } from "next/router";

function pathToLabel(path: string): string {
  if (path === "/") return "Home";
  const segment = path.replace(/^\//, "").split("/")[0];
  return segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function BlogBreadcrumbs({ postTitle }: { postTitle: string }) {
  const router = useRouter();
  const from = router.query.from as string | undefined;
  const parentHref = from && from !== "/" ? from : "/nieuws";
  const parentLabel = from && from !== "/" ? pathToLabel(from) : "Nieuws";

  const items = [
    { label: "Home", href: "/" },
    { label: parentLabel, href: parentHref },
    { label: postTitle },
  ];

  return <Breadcrumbs items={items} />;
}

export default function BlogPostPage({
  post,
  settings,
}: {
  post: IBlogPost;
  settings: ISettings;
}) {
  return (
    <div className="bg-grey">
      <Meta
        settings={settings}
        data={{
          title: post.title,
          description: "",
          noIndex: false,
          keywords: "",
          image: post.image?.image,
        }}
      />
      <BlogBreadcrumbs postTitle={post.title} />

      <div className="component-padding pb-16">
        <div className="max-w-4xl m-auto flex flex-col gap-8">
          {post.image?.image && (
            <Image
              width={1200}
              height={675}
              sizes="(max-width: 896px) 100vw, 896px"
              alt={post.image?.caption || post.title}
              src={urlFor(post.image.image).url()}
              className="w-full object-cover aspect-video"
              priority
            />
          )}

          <h1 className="text-3xl md:text-4xl lg:text-5xl">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 font-body">
            {post.date && (
              <span>
                {format(new Date(post.date), "d MMMM yyyy", { locale: nl })}
              </span>
            )}
            {post.date && post.tags?.length && <span className="opacity-40">&middot;</span>}
            <span className="opacity-60">{post.tags?.join(" / ")}</span>
          </div>

          {post.content?.script && <Pt data={post.content.script} />}
        </div>
      </div>

      <Footer data={settings} />
    </div>
  );
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const params = ctx.params as { slug: string };
  const draftMode = ctx.draftMode ?? false;
  const client = getClient(draftMode);
  const query = `*[_type == "blogPost" && slug.current == $slug]{
    ...,
    content {
      ...,
      script[]{
        ...,
        "playbackId": video.asset->playbackId,
        _type == "partners" => {
          ...,
          "partners": select(
            allPartners == true => *[_type == "partner"],
            partners[]->
          )
        },
        _type == "media" => {
          ...,
          gallery {
            ...,
            items[]{
              ...,
              "playbackId": video.asset->playbackId
            }
          }
        }
      }
    }
  }`;

  const settings = await client.fetch(settingsQuery);
  const posts = await client.fetch(query, { slug: params.slug });

  if (!posts || posts.length === 0) {
    return { notFound: true };
  }

  return {
    props: {
      post: posts[0],
      settings,
      draftMode,
    },
    revalidate: 5,
  };
}

export async function getStaticPaths() {
  const query = `*[_type == "blogPost"]{ "slug": slug.current }`;
  const posts = await client.fetch(query);

  const paths =
    posts?.map((post: { slug: string }) => ({
      params: { slug: post.slug },
    })) || [];

  return {
    paths,
    fallback: "blocking",
  };
}
